import { isB2B } from '@/core/settings/utils';
import type { Product } from '@/domains/productDiscovery/algolia/interfaces/product';
import { productHasDiscount } from '@/domains/productDiscovery/CommercialAnimation/utils/getProductPromoTags';
import {
  MiddleLabel,
  productLabels,
  ThirdLineLabel,
  TopLeftLabel,
} from '@/domains/productDiscovery/Listings/components/ProductCardListingsEnhancer/components/ProductCardListings/interfaces';
import { WithSecondHandProduct } from '@/domains/productDiscovery/Listings/products/secondHand/addSecondHandProductInfo';

export const pickTopLeftLabel = ({
  product,
  couponLabelDisabled = false,
  bulkLabelEnabled = false,
  proPriceAdvantageLabelEnabled = false,
}: {
  product: Product;
  couponLabelDisabled?: boolean;
  bulkLabelEnabled?: boolean;
  proPriceAdvantageLabelEnabled?: boolean;
}): TopLeftLabel[] | undefined => {
  if (product.bulk && bulkLabelEnabled) return [productLabels.BULK];

  if (product.coupon && !couponLabelDisabled) return [productLabels.COUPON];

  if (
    isB2B() &&
    product.proAdvantagePercentage &&
    product.proAdvantagePercentage > 0 &&
    proPriceAdvantageLabelEnabled
  ) {
    return [productLabels.PRO_PRICE_ADVANTAGE];
  }

  return undefined;
};

export const pickMiddleLabels = ({
  product,
  isSponsored = false,
  rankingAbtest = false,
  technicalInfoEnabled = false,
}: {
  product: Product & WithSecondHandProduct;
  isSponsored?: boolean;
  rankingAbtest?: boolean;
  technicalInfoEnabled?: boolean;
}): MiddleLabel[] => {
  const middleLabels: MiddleLabel[] = [];
  const isLabelAdditionAllowed =
    middleLabels.length === 0 ||
    (middleLabels.length === 1 && middleLabels[0] === productLabels.SPONSORED);
  const bannerKey = rankingAbtest ? 'bannerAlternate' : 'banner';
  const commercialAnimationTags = product.commercialAnimation?.tags;
  const hasDiscount = productHasDiscount(
    product.prices.main,
    product.prices.retail,
    product.prices.discount,
  );

  if (isSponsored) {
    middleLabels.push(productLabels.SPONSORED);
  }

  if (!!commercialAnimationTags?.length && hasDiscount) {
    if (
      commercialAnimationTags.includes(productLabels.BLACKFRIDAY.toLowerCase())
    ) {
      middleLabels.push(productLabels.BLACKFRIDAY);
    } else if (
      commercialAnimationTags.includes(productLabels.MANOMANODAYS.toLowerCase())
    ) {
      middleLabels.push(productLabels.MANOMANODAYS);
    } else if (
      commercialAnimationTags.includes(productLabels.SALES.toLowerCase())
    ) {
      middleLabels.push(productLabels.SALES);
    } else if (
      commercialAnimationTags.includes(productLabels.PROMO.toLowerCase())
    ) {
      middleLabels.push(productLabels.PROMO);
    }
  }
  if (
    !!commercialAnimationTags?.length &&
    commercialAnimationTags.includes(productLabels.LOWERED_PRICE)
  ) {
    middleLabels.push(productLabels.LOWERED_PRICE);
  }

  if (isLabelAdditionAllowed) {
    if (
      product[bannerKey]?.categories.includes(
        `topSales.${product.categoriesLastId}`,
      )
    ) {
      middleLabels.push(productLabels.TOP_SALE);
    } else if (product.isEcoResponsible && !technicalInfoEnabled) {
      middleLabels.push(productLabels.ECO_RESPONSIBLE);
    } else if (product.isLocal && !technicalInfoEnabled) {
      middleLabels.push(productLabels.MADE_IN);
    } else if (product.isBestQualityOffer) {
      middleLabels.push(productLabels.BEST_QUALITY_OFFER);
    } else if (product.isSecondHand) {
      middleLabels.push(productLabels.SECOND_HAND);
    }
  }

  return middleLabels;
};

export const pickThirdLineLabels = ({
  product,
  technicalInfoEnabled,
}: {
  product: Product;
  technicalInfoEnabled: boolean;
}): ThirdLineLabel[] => {
  if (!technicalInfoEnabled) return [];

  const thirdLineLabels: ThirdLineLabel[] = [];

  if (product.technicalInfoValues) {
    product.technicalInfoValues.map((label) => thirdLineLabels.unshift(label));
  }

  if (product.isEcoResponsible) {
    thirdLineLabels.unshift(productLabels.ECO_RESPONSIBLE);
  } else if (product.isLocal) {
    thirdLineLabels.unshift(productLabels.MADE_IN);
  }

  return thirdLineLabels;
};
